import { useState } from "react";
import type { RecordOut } from "../../api/types";
import { Icon } from "../../components/Icon";
import { TriCheckbox, cx } from "../../components/ui";
import { formatMoney, formatPrice, formatUsd, isNegative, isZero, parseDecimal } from "../../lib/decimal";
import { formatDateTime } from "../../lib/dates";

/**
 * Columns transcribed from frontend/components/records_table_model.COLUMNS:
 * same order, same captions, same sort keys. `numeric` right-aligns, as the Qt
 * model does via its text alignment role.
 */
export interface Column {
  caption: string;
  /** RecordOut field the server sorts by. */
  key: string;
  numeric: boolean;
  render: (record: RecordOut) => React.ReactNode;
  /** Hidden on narrow screens to keep the important columns readable. */
  secondary?: boolean;
}

export const COLUMNS: Column[] = [
  { caption: "DATE", key: "created_at", numeric: false, render: (r) => formatDateTime(r.created_at) },
  { caption: "CLIENT", key: "client_id", numeric: false, render: (r) => r.client_id },
  { caption: "ACCOUNT", key: "account_number", numeric: false, render: (r) => r.account_number },
  { caption: "TICKET", key: "ticket_number", numeric: false, render: (r) => r.ticket_number },
  { caption: "PLATFORM", key: "platform", numeric: false, render: (r) => r.platform, secondary: true },
  { caption: "SYMBOL", key: "symbol", numeric: false, render: (r) => r.symbol },
  { caption: "LOT", key: "lot", numeric: true, render: (r) => formatPrice(r.lot) },
  { caption: "OPEN", key: "open_price", numeric: true, render: (r) => formatPrice(r.open_price), secondary: true },
  { caption: "SL", key: "sl_price", numeric: true, render: (r) => formatPrice(r.sl_price), secondary: true },
  { caption: "CLOSE", key: "close_price", numeric: true, render: (r) => formatPrice(r.close_price), secondary: true },
  { caption: "PROFIT", key: "profit", numeric: true, render: (r) => <Signed value={r.profit} /> },
  {
    caption: "COMPENSATION",
    key: "compensation_amount",
    numeric: true,
    render: (r) => formatMoney(r.compensation_amount),
  },
  {
    caption: "FINAL",
    key: "final",
    numeric: true,
    render: (r) => <Signed value={r.final_amount} positiveColor />,
  },
  { caption: "REASON", key: "reason", numeric: false, render: (r) => r.reason, secondary: true },
  {
    caption: "OPERATOR",
    key: "operator_name",
    numeric: false,
    render: (r) => r.operator_name ?? "—",
    secondary: true,
  },
];

/**
 * `positiveColor` also greens out a positive value — used for FINAL, where a
 * positive number is money going out to the client and worth flagging, same
 * as a negative one already is.
 */
function Signed({ value, positiveColor = false }: { value: string; positiveColor?: boolean }) {
  const parsed = parseDecimal(value);
  const negative = parsed !== null && isNegative(parsed);
  const positive = positiveColor && parsed !== null && !negative && !isZero(parsed);
  return (
    <span className={cx(negative && "text-danger", positive && "text-success")}>
      {formatMoney(value)}
    </span>
  );
}

export interface FlagHeaderState {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
}

interface TableProps {
  rows: RecordOut[];
  orderBy: string;
  descending: boolean;
  onSort: (key: string) => void;
  doneHeader: FlagHeaderState;
  rejectHeader: FlagHeaderState;
  onSetFlagMatching: (flag: "done" | "rejected", value: boolean) => void;
  onSetFlagOne: (flag: "done" | "rejected", record: RecordOut, value: boolean) => void;
  onOpen: (record: RecordOut) => void;
  onEdit: (record: RecordOut) => void;
  onDuplicate: (record: RecordOut) => void;
  onDelete: (record: RecordOut) => void;
  /** Group-delete selection — a client-side tick, not a persisted field. */
  selectedIds: Set<number>;
  selectHeader: FlagHeaderState;
  onToggleSelectAll: (value: boolean) => void;
  onToggleSelect: (record: RecordOut, value: boolean) => void;
}

export function RecordsTable(props: TableProps) {
  const {
    rows,
    orderBy,
    descending,
    onSort,
    doneHeader,
    rejectHeader,
    onSetFlagMatching,
    onSetFlagOne,
    onOpen,
    selectedIds,
    selectHeader,
    onToggleSelectAll,
    onToggleSelect,
  } = props;

  // A single click just marks the row so it's easy to keep your place in a
  // long list — it does not open it. Opening needs a deliberate action: the
  // "view" icon, or a double-click.
  const [markedId, setMarkedId] = useState<number | null>(null);

  return (
    // Bounded height (rather than letting the table grow with however many
    // rows are on the page) keeps both scrollbars at a fixed, always-visible
    // edge of this box — a horizontal scrollbar hundreds of rows below the
    // fold is effectively undiscoverable.
    <div className="card max-h-[65vh] overflow-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-line">
            {/* The two status ticks. The checkbox acts on every record the
                current search matches — not just this page — which is why it
                reports tri-state against the whole result set. The caption
                itself is a sort button, same as every other column header. */}
            <FlagHeader
              caption="DONE"
              sortKey="is_done"
              state={doneHeader}
              orderBy={orderBy}
              descending={descending}
              onChange={(value) => onSetFlagMatching("done", value)}
              onSort={onSort}
            />
            <FlagHeader
              caption="REJECT"
              sortKey="is_rejected"
              state={rejectHeader}
              orderBy={orderBy}
              descending={descending}
              onChange={(value) => onSetFlagMatching("rejected", value)}
              onSort={onSort}
            />
            <SelectHeader caption="DELETE" state={selectHeader} onChange={onToggleSelectAll} />

            {COLUMNS.map((column) => (
              <th
                key={column.key}
                className={cx(
                  "sticky top-0 z-10 border-b border-line bg-surface px-3 py-2.5 whitespace-nowrap",
                  column.numeric ? "text-right" : "text-left",
                  column.secondary && "hidden xl:table-cell",
                )}
              >
                <button
                  onClick={() => onSort(column.key)}
                  className="col-head inline-flex cursor-pointer items-center gap-1 hover:text-fg"
                >
                  {column.caption}
                  {orderBy === column.key ? (
                    <Icon name={descending ? "chevron-down" : "chevron-up"} className="size-3" />
                  ) : null}
                </button>
              </th>
            ))}
            <th className="sticky top-0 z-10 border-b border-line bg-surface px-3 py-2.5" />
          </tr>
        </thead>

        <tbody>
          {rows.map((record, index) => (
            <tr
              key={record.id}
              onClick={() => setMarkedId((current) => (current === record.id ? null : record.id))}
              onDoubleClick={() => onOpen(record)}
              className={cx(
                "cursor-pointer border-b border-line/60 last:border-0 hover:bg-hover",
                index % 2 === 1 && "bg-alt-row",
                markedId === record.id && "bg-selection hover:bg-selection",
              )}
            >
              <td className="px-3 py-2">
                <TriCheckbox
                  label={`Mark record ${record.id} done`}
                  checked={record.is_done}
                  onChange={(value) => onSetFlagOne("done", record, value)}
                />
              </td>
              <td className="px-3 py-2">
                <TriCheckbox
                  label={`Mark record ${record.id} rejected`}
                  checked={record.is_rejected}
                  onChange={(value) => onSetFlagOne("rejected", record, value)}
                />
              </td>
              <td className="px-3 py-2">
                <TriCheckbox
                  label={`Select record ${record.id} for deletion`}
                  checked={selectedIds.has(record.id)}
                  onChange={(value) => onToggleSelect(record, value)}
                />
              </td>
              {COLUMNS.map((column) => (
                <td
                  key={column.key}
                  className={cx(
                    "px-3 py-2 whitespace-nowrap",
                    column.numeric ? "num text-right" : "text-left",
                    column.secondary && "hidden xl:table-cell",
                  )}
                >
                  {column.render(record)}
                </td>
              ))}
              <td className="px-3 py-2 text-right">
                <RowActions record={record} {...props} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FlagHeader({
  caption,
  sortKey,
  state,
  orderBy,
  descending,
  onChange,
  onSort,
}: {
  caption: string;
  sortKey: string;
  state: FlagHeaderState;
  orderBy: string;
  descending: boolean;
  onChange: (value: boolean) => void;
  onSort: (key: string) => void;
}) {
  return (
    <th className="sticky top-0 z-10 border-b border-line bg-surface px-3 py-2.5 text-left">
      <div className="flex items-center gap-2">
        <TriCheckbox
          label={`Mark all matching records ${caption.toLowerCase()}`}
          checked={state.checked}
          indeterminate={state.indeterminate}
          disabled={state.disabled}
          onChange={onChange}
        />
        <button
          onClick={() => onSort(sortKey)}
          className="col-head inline-flex cursor-pointer items-center gap-1 hover:text-fg"
        >
          {caption}
          {orderBy === sortKey ? (
            <Icon name={descending ? "chevron-down" : "chevron-up"} className="size-3" />
          ) : null}
        </button>
      </div>
    </th>
  );
}

/** Header tick for the group-delete column — selection only, not sortable. */
function SelectHeader({
  caption,
  state,
  onChange,
}: {
  caption: string;
  state: FlagHeaderState;
  onChange: (value: boolean) => void;
}) {
  return (
    <th className="sticky top-0 z-10 border-b border-line bg-surface px-3 py-2.5 text-left">
      <div className="flex items-center gap-2">
        <TriCheckbox
          label={`Select all rows on this page for deletion`}
          checked={state.checked}
          indeterminate={state.indeterminate}
          disabled={state.disabled}
          onChange={onChange}
        />
        <span className="col-head text-danger">{caption}</span>
      </div>
    </th>
  );
}

function RowActions({
  record,
  onOpen,
  onEdit,
  onDuplicate,
  onDelete,
}: { record: RecordOut } & Pick<TableProps, "onOpen" | "onEdit" | "onDuplicate" | "onDelete">) {
  return (
    <div className="flex items-center justify-end gap-1" onClick={(event) => event.stopPropagation()}>
      <IconAction label="View details" icon="eye" onClick={() => onOpen(record)} />
      <IconAction label="Edit" icon="edit" onClick={() => onEdit(record)} />
      <IconAction label="Duplicate" icon="copy" onClick={() => onDuplicate(record)} />
      <IconAction label="Delete" icon="trash" danger onClick={() => onDelete(record)} />
    </div>
  );
}

function IconAction({
  label,
  icon,
  onClick,
  danger = false,
}: {
  label: string;
  icon: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cx(
        "cursor-pointer rounded-md p-1.5 transition-colors",
        danger ? "text-fg-muted hover:bg-danger/15 hover:text-danger" : "text-fg-muted hover:bg-hover hover:text-fg",
      )}
    >
      <Icon name={icon} className="size-4" />
    </button>
  );
}

/** Phone layout: one card per record instead of a 17-column table. */
export function RecordsCards({
  rows,
  onSetFlagOne,
  onOpen,
  onEdit,
  onDuplicate,
  onDelete,
  selectedIds,
  onToggleSelect,
}: Pick<
  TableProps,
  "rows" | "onSetFlagOne" | "onOpen" | "onEdit" | "onDuplicate" | "onDelete" | "selectedIds" | "onToggleSelect"
>) {
  return (
    <div className="flex flex-col gap-2">
      {rows.map((record) => (
        <div key={record.id} className="card p-3">
          <div className="flex items-start gap-3">
            <button onClick={() => onOpen(record)} className="min-w-0 flex-1 cursor-pointer text-left">
              <div className="flex items-baseline justify-between gap-2">
                <span className="num truncate font-medium">{record.ticket_number}</span>
                <span className="num shrink-0 font-semibold text-accent">
                  {formatUsd(record.compensation_amount)}
                </span>
              </div>
              <p className="mt-0.5 truncate text-xs text-fg-secondary">
                {record.symbol} · {record.platform} · lot {formatPrice(record.lot)}
              </p>
              <p className="mt-0.5 truncate text-xs text-fg-muted">
                {record.client_id} · {record.account_number} · {formatDateTime(record.created_at)}
              </p>
              <p className="mt-1 truncate text-xs text-fg-muted">{record.reason}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span className="num text-xs text-fg-secondary">
                  profit {formatMoney(record.profit)} → final {formatMoney(record.final_amount)}
                </span>
              </div>
            </button>
          </div>

          <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-2.5">
            <div className="flex items-center gap-3">
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-fg-secondary">
                <TriCheckbox
                  label={`Mark record ${record.id} done`}
                  checked={record.is_done}
                  onChange={(value) => onSetFlagOne("done", record, value)}
                />
                Done
              </label>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-fg-secondary">
                <TriCheckbox
                  label={`Mark record ${record.id} rejected`}
                  checked={record.is_rejected}
                  onChange={(value) => onSetFlagOne("rejected", record, value)}
                />
                Rejected
              </label>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-danger">
                <TriCheckbox
                  label={`Select record ${record.id} for deletion`}
                  checked={selectedIds.has(record.id)}
                  onChange={(value) => onToggleSelect(record, value)}
                />
                Delete
              </label>
            </div>
            <div className="flex items-center gap-1">
              <IconAction label="Edit" icon="edit" onClick={() => onEdit(record)} />
              <IconAction label="Duplicate" icon="copy" onClick={() => onDuplicate(record)} />
              <IconAction label="Delete" icon="trash" danger onClick={() => onDelete(record)} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
